import { mapMetadataSubmissions, metadataFieldBuilder } from '@/components/UI/FormFields/FormField.utils';
import BenchlingEntityLink from '@/components/UI/BenchlingEntityLink/BenchlingEntityLink';
import Link from '@/components/UI/Link';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { fetchMetadataColumns, formatNumber } from '@/helpers';
import { _isNil, _isNotEmpty, _notNil, _toNumber } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { MetadataField } from '@/model/Metadata.model';
import type {
  ClassificationType,
  Treatment,
  TreatmentApiId,
  TreatmentCreateV1,
  TreatmentField,
  TreatmentFieldV1,
  TreatmentMethod,
  TreatmentVolumeUnit,
} from '@/model/Treatment.model';
import { structuredCloneUtil } from '@/utils/browser.utils';

export type ClassificationTypeDetails = {
  key: string;
  title: string;
  plural: string;
  metadata: string;
  sublabel?: string;
};

export const classificationTypes: Record<ClassificationType, ClassificationTypeDetails> = {
  treatment: {
    key: 'treatment',
    title: 'Treatment',
    plural: 'treatments',
    metadata: 'treatment_meta',
    sublabel: 'Administered only to animals in groups',
  },
  disease_induction: { key: 'disease', title: 'Disease', plural: 'diseases', metadata: 'disease_meta' },
  pre_treatment: {
    key: 'pre-treatment',
    title: 'Pre-treatment',
    plural: 'pre-treatments',
    metadata: 'treatment_meta',
    sublabel: 'Administered to any animal',
  },
} as const;

export const generateTreatmentColumns = (
  isWriteUserForStudy: boolean,
  duplicates: Set<TreatmentApiId>,
  isMounted: () => boolean,
  handleAddOrUpdateTreatmentsClick: (event: React.MouseEvent, treatment?: Treatment | undefined) => Promise<boolean>,
  metadata: Array<MetadataField>,
  classification: ClassificationType
): Array<TanStackColumnDef<Treatment>> => [
  {
    id: 'id',
    accessorKey: 'id',
    size: 50,
    isVisible: false,
    enableSorting: true,
  },
  {
    id: 'treatment_label',
    header: 'Label',
    cell: (props: {
      row: {
        original: Treatment;
      };
      table?: any;
    }) => {
      const { row, table } = props;
      const apiTable = table?.options?.meta?.apiTable;
      const setUpdating = apiTable?.setUpdating;
      const fetchTableData = apiTable?.fetchTableData;
      const { id, api_id } = row.original;
      const original = row.original;

      return (
        <>
          {isWriteUserForStudy ? (
            <div
              className="flex items-center pointer"
              data-test-component="TreatmentLabelCell"
              data-test-element="container"
              data-test-key={`${id}`}
            >
              <BenchlingEntityLink
                entityType="treatments"
                apiId={original.api_id}
                benchlingEntityWebUrl={original.benchling_entity_web_url}
                visible={original.synced_to_benchling === true}
              />
              <Link
                className="link w-100"
                openModal
                onClick={(e) => {
                  setUpdating(true);
                  handleAddOrUpdateTreatmentsClick(e, original)
                    .then((reload) => {
                      if (reload) {
                        return fetchTableData();
                      }
                    })
                    .finally(() => {
                      if (isMounted()) {
                        setUpdating(false);
                      }
                    })
                    .catch(() => {
                      if (isMounted()) {
                        setUpdating(false);
                      }
                    });
                }}
              >
                {original.treatment_label}
                {duplicates.has(api_id) && <span className="ml2 dark-gray">(duplicate)</span>}
              </Link>
            </div>
          ) : (
            <div className="flex items-center">
              <BenchlingEntityLink
                entityType="treatments"
                apiId={original.api_id}
                benchlingEntityWebUrl={original.benchling_entity_web_url}
                visible={original.synced_to_benchling === true}
              />
              <span>
                {original.treatment_label}
                {duplicates.has(api_id) && <span className="ml2 dark-gray">(duplicate)</span>}
              </span>
            </div>
          )}
        </>
      );
    },
    isVisible: true,
    enableSorting: true,
    accessorKey: 'treatment_label',
  },
  {
    id: 'treatment_name',
    header: 'Name',
    cell: (props: {
      row: {
        original: Treatment;
      };
      table?: any;
    }) => {
      const { row } = props;
      const { treatment_type: treatmentType, id, api_id } = row.original;
      const original = row.original;

      return (
        <>
          <div
            className="flex items-center"
            data-test-component="TreatmentNameCell"
            data-test-element="container"
            data-test-key={`${id}`}
          >
            {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
            {original.treatment_type?.benchling_link ? (
              <a
                className="blue pointer ph2 pv1 br2 no-underline"
                style={{ backgroundColor: '#e5e5e5' }}
                data-tooltip-id="global-tooltip-id"
                data-tooltip-content="Click to view in Benchling"
                onClick={() => handleIconClick(original)}
              >
                {treatmentType?.name}
              </a>
            ) : (
              <span>{treatmentType?.name}</span>
            )}
            {duplicates.has(api_id) && <span className="ml2 dark-gray">(duplicate)</span>}
          </div>
        </>
      );
    },
    isVisible: true,
    enableSorting: true,
    accessorKey: 'name',
  },
  {
    id: 'treatment_classification',
    header: 'Classification',
    cell: ({ row: { original } }: { row: { original: Treatment } }) =>
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      original.treatment_classification ? classificationTypes[original.treatment_classification].title : '-',
    isVisible: classification === 'treatment',
    enableSorting: true,
  },
  {
    id: 'dose_concentration',
    header: 'Dose',
    cell: ({ row: { original } }: { row: { original: Treatment } }) => {
      const currentField = original.fields.find((x) => x.name === 'dose');
      return formatDosingColumnValue(currentField);
    },
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'dose_volume',
    header: 'Dose volume',
    cell: ({ row: { original } }: { row: { original: Treatment } }) => {
      const currentField = original.fields.find((x) => x.name === 'dose_volume');
      return formatDosingColumnValue(currentField);
    },
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'stock_concentration',
    header: 'Stock Concentration',
    cell: ({ row: { original } }: { row: { original: Treatment } }) => {
      const currentField = original.fields.find((x) => x.name === 'stock');
      return formatDosingColumnValue(currentField);
    },
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'volume',
    header: 'Volume',
    cell: ({ row: { original } }: { row: { original: Treatment } }) => {
      const currentField = original.fields.find((x) => x.name === 'volume');
      return formatDosingColumnValue(currentField);
    },
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'groups',
    header: 'Groups',
    cell: ({ row: { original } }: { row: { original: Treatment } }) =>
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      original.study_groups?.map?.(({ name }) => name).join(', ') || '-',
    isVisible: true,
    enableSorting: false,
    accessorFn: (row) => row.study_groups,
  },
  ...fetchMetadataColumns({ metadata }),
];

export const generateTreatmentColumnsStep = (metadata: Array<MetadataField>): Array<TanStackColumnDef<Treatment>> => [
  {
    id: 'treatment_label',
    header: 'Label',
    cell: ({ row: { original } }: { row: { original: Treatment } }) => original.treatment_label,
    isVisible: true,
    enableSorting: true,
    accessorFn: (row) => row.treatment_label,
  },
  {
    id: 'treatment_name',
    header: 'Name',
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    cell: ({ row: { original } }: { row: { original: Treatment } }) => original.treatment_type?.name || '-',
    isVisible: true,
    enableSorting: true,
    accessorFn: (row) => row.treatment_type?.name,
  },
  {
    id: 'dose_concentration',
    header: 'Dose',
    cell: ({ row: { original } }: { row: { original: Treatment } }) => {
      const currentField = original.fields.find((x) => x.name === 'dose');
      return formatDosingColumnValue(currentField);
    },
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'dose_volume',
    header: 'Dose volume',
    cell: ({ row: { original } }: { row: { original: Treatment } }) => {
      const currentField = original.fields.find((x) => x.name === 'dose_volume');
      return formatDosingColumnValue(currentField);
    },
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'stock_concentration',
    header: 'Stock Concentration',
    cell: ({ row: { original } }: { row: { original: Treatment } }) => {
      const currentField = original.fields.find((x) => x.name === 'stock');
      return formatDosingColumnValue(currentField);
    },
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'volume',
    header: 'Volume',
    cell: ({ row: { original } }: { row: { original: Treatment } }) => {
      const currentField = original.fields.find((x) => x.name === 'volume');
      return formatDosingColumnValue(currentField);
    },
    isVisible: true,
    enableSorting: true,
  },
  {
    id: 'groups',
    header: 'Groups',
    cell: ({ row: { original } }: { row: { original: Treatment } }) =>
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      original.study_groups?.map?.(({ name }) => name).join(', ') || '-',
    isVisible: true,
    enableSorting: false,
    accessorFn: (row) => row.study_groups,
  },
  ...fetchMetadataColumns({ metadata }),
];

export const handleIconClick = (treatment: Treatment) => {
  window.open(treatment?.treatment_type?.benchling_link ?? undefined, '_blank', 'noopener noreferrer');
};

export const formatDosingColumnValue = (treatmentField: TreatmentField | TreatmentFieldV1 | undefined) => {
  if (_notNil(treatmentField)) {
    // @ts-expect-error: unit name may not exist
    const unit = `${treatmentField.unit.display_unit ?? treatmentField.unit.name}`;

    if (_isNotEmpty(treatmentField.default_display_value)) {
      return `${formatNumber(treatmentField.default_display_value, true, 3)} ${unit}`;
    }

    if (_isNotEmpty(treatmentField.default_value)) {
      return `${formatNumber(treatmentField.default_value, true, 3)} ${unit}`;
    }
  }
  return '-';
};

export const updateNumberToString = (fields: Array<TreatmentField>): Array<TreatmentField> =>
  fields.map((field) => ({
    ...field,
    default_value: _toNumber(field.default_value)?.toString(),
  }));

export type TreatmentFromForm = Treatment & {
  meta: unknown;
  dosingMethod: TreatmentMethod;
  treatment_type_id?: ID;
  treatment_volume_unit?: TreatmentVolumeUnit;
};

export const addAdditionalProperties = (
  formItems: TreatmentFromForm,
  metadata?: Array<MetadataField>,
  type?: ClassificationType
): TreatmentCreateV1 => {
  const updatedItems = structuredCloneUtil(formItems);

  if (_isNil(formItems.type)) {
    updatedItems.type = formItems.dosingMethod;
  }

  updatedItems.fields = updatedItems.fields.map(mapFormPropertiesToTreatment);
  updatedItems.treatment_type_id = formItems.treatment_type?.id;

  if (_isNotEmpty(metadata)) {
    updatedItems.metadata = mapMetadataSubmissions(formItems.meta, metadata);
  }

  const metadataUpdate = (updatedItems.metadata ?? []).map(({ glossary_id, value }) => ({
    glossary_id: glossary_id as number,
    value,
  }));

  const fieldsUpdate = (updatedItems.fields ?? []).map(({ name, default_value, unit, required }) => ({
    name,
    value: default_value?.toString() ?? null,
    unit_id: unit.id as number,
    required: required ?? false,
  }));

  return {
    type: updatedItems.type,
    treatment_type_id: updatedItems.treatment_type_id as number,
    treatment_label: updatedItems.treatment_label,
    treatment_type: updatedItems.treatment_type,
    treatment_classification: type ?? 'treatment',
    treatment_volume_unit: updatedItems.treatment_volume_unit,
    fields: fieldsUpdate,
    metadata: metadataUpdate,
  };
};

// Workaround while study create still uses PHP to create groups & treatments
export const addAdditionalStudyCreateProperties = (
  formItems: TreatmentFromForm,
  metadata?: Array<MetadataField>,
  treatment?: Treatment,
  type?: ClassificationType
) => {
  const updatedItems = structuredCloneUtil(formItems);

  if (_isNil(formItems.type)) {
    updatedItems.type = formItems.dosingMethod;
  }

  updatedItems.fields = updatedItems.fields.map(mapFormPropertiesToTreatment);
  updatedItems.treatment_type_id = formItems.treatment_type?.id;

  if (_isNotEmpty(treatment?.study_groups)) {
    // @ts-expect-error: mapped from ts-ignore
    updatedItems.study_groups = treatment?.study_groups.map(({ id }) => id);
  } else {
    delete updatedItems.study_groups;
  }
  if (_isNotEmpty(metadata)) {
    updatedItems.metadata = mapMetadataSubmissions(formItems.meta, metadata);
  }

  if (_isNil(type) && _notNil(formItems.treatment_classification)) {
    type = formItems.treatment_classification;
  }
  updatedItems.treatment_classification = type ?? 'treatment';

  return updatedItems;
};

interface MapFormPropertiesToTreatmentReturn extends TreatmentField {
  unit_id: ID;
  value?: string;
  name: string;
}

export const mapFormPropertiesToTreatment = (field: TreatmentField): MapFormPropertiesToTreatmentReturn => ({
  ...field,
  unit_id: field.unit?.id ?? '',
  value: field.default_value?.toString(),
  name: field.unit?.unit_type ?? '',
});

export const treatmentMetadataFields = (treatmentMetas: Array<MetadataField>, treatment: Treatment) =>
  (treatmentMetas ?? []).map((meta) => metadataFieldBuilder(meta, treatment));

export const getQueryParams = (type: ClassificationType): Record<string, string> => {
  let classification = '';
  switch (type) {
    case 'disease_induction':
      classification = 'disease_induction';
      break;
    case 'treatment':
      classification = 'treatment,pre_treatment';
      break;
  }
  return { classification };
};
